/* ==========================================================================
   Collection grid quick add — behaviour
   Paired with snippets/grid-quick-add.liquid and assets/grid-quick-add.css

   Cookies / storage written: none.
   Network: none directly. Adding to cart is delegated to the theme's
   productAddButtonForm controller (assets/theme.js), which POSTs to the
   first-party {routes.cart}/add.js and GETs {routes.cart}.js.
   Tracking events fired: none.
   ========================================================================== */

const SEL = {
  trigger: '[data-qa-trigger]',
  panel: '[data-qa-panel]',
  swatch: '[data-qa-swatch]',
  confirm: '[data-qa-confirm]',
  confirmLabel: '[data-qa-confirm-label]',
  close: '[data-qa-close]',
  selected: '[data-qa-selected]',
  live: '[data-qa-live]',
  idInput: 'form input[name="id"]',
};

const LIVE_CLEAR_MS = 5000;

class GridQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.trigger = this.querySelector(SEL.trigger);
    this.panel = this.querySelector(SEL.panel);
    this.swatches = Array.from(this.querySelectorAll(SEL.swatch));
    this.confirm = this.querySelector(SEL.confirm);
    this.confirmLabel = this.querySelector(SEL.confirmLabel);
    this.closeBtn = this.querySelector(SEL.close);
    this.selected = this.querySelector(SEL.selected);
    this.live = this.querySelector(SEL.live);
    this.idInput = this.querySelector(SEL.idInput);

    this.labels = {
      add: this.dataset.labelAdd || 'Add to Cart',
      addShort: this.dataset.labelAddShort || this.dataset.labelAdd || 'Add',
      added: this.dataset.labelAdded || 'Added',
      addedToCart: this.dataset.labelAddedToCart || 'Added to Cart',
      soldOut: this.dataset.labelSoldOut || 'Sold Out',
    };
    this.productTitle = this.dataset.productTitle || '';
    this.closeDelay = parseInt(this.dataset.closeDelay, 10) || 1400;
    this.addedDuration = parseInt(this.dataset.addedDuration, 10) || 2000;

    this.loading = false;
    this.wasSuccess = false;
    this.variantTitle = '';
    this.baseLabel = this.confirmLabel ? this.confirmLabel.textContent.trim() : '';

    if (!this.confirm) return;

    this.bind();

    const pressed = this.swatches.find((s) => s.getAttribute('aria-pressed') === 'true');
    if (pressed) this.variantTitle = pressed.dataset.value || '';
  }

  bind() {
    if (this.trigger) {
      this.trigger.addEventListener('click', () => this.open());
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close(true));
    }

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => this.select(swatch));
    });

    // Capture phase so a second click during an in-flight add never reaches
    // the theme controller's own click handler.
    this.confirm.addEventListener(
      'click',
      (event) => {
        if (this.loading) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.panel && !this.panel.hidden) {
        event.preventDefault();
        this.close(true);
      }
    });

    // Dispatched by the theme controller via x-effect on the confirm button.
    this.addEventListener('quickadd:state', (event) => this.onState(event.detail || {}));
  }

  /* ---------------------------------------------------------------- panel */

  open() {
    if (!this.panel) return;
    this.panel.hidden = false;
    this.trigger.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'true');

    // The trigger leaves the tab order when it hides, so focus must move
    // into the panel or a keyboard user is dropped at <body>.
    const pressed = this.swatches.find((s) => s.getAttribute('aria-pressed') === 'true');
    const target = pressed || this.swatches[0] || this.confirm;
    this.refreshCarousel();
    window.requestAnimationFrame(() => target.focus());
  }

  close(refocus) {
    if (!this.panel || this.panel.hidden) return;
    const focusWasInside = this.panel.contains(document.activeElement);
    this.panel.hidden = true;
    this.trigger.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.refreshCarousel();
    // Only reclaim focus when it was ours to begin with. After an add the
    // cart drawer may have taken focus, and it must keep it.
    if (refocus || focusWasInside) this.trigger.focus();
  }

  /**
   * Inside a Flickity slider the viewport has a fixed height and clips its
   * cells, so an open panel would be cut off. Ask Flickity to re-measure with
   * the equal-height min-height lifted, so it can both grow and shrink.
   */
  refreshCarousel() {
    const grid = this.closest('.flickity-enabled');
    if (!grid) return;
    // The theme's <flickity-carousel> element keeps the instance on
    // carouselInstance; Flickity.data() is only a fallback.
    const host = grid.closest('flickity-carousel');
    const Flickity = window.Flickity;
    const instance =
      (host && host.carouselInstance) ||
      (Flickity && typeof Flickity.data === 'function' ? Flickity.data(grid) : null);
    grid.classList.add('is-measuring');
    if (instance && typeof instance.resize === 'function') {
      instance.resize();
      grid.classList.remove('is-measuring');
      return;
    }
    // No instance handle: fall back to Flickity's own window resize listener.
    window.dispatchEvent(new Event('resize'));
    window.setTimeout(() => {
      grid.classList.remove('is-measuring');
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }

  select(swatch) {
    this.swatches.forEach((s) => {
      s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false');
    });

    if (this.idInput) this.idInput.value = swatch.dataset.variantId;
    if (this.selected) this.selected.textContent = swatch.dataset.value || '';
    this.variantTitle = swatch.dataset.value || '';

    const available = swatch.dataset.available === 'true';
    if (available) {
      this.confirm.disabled = false;
      this.baseLabel = `${this.labels.addShort} — ${swatch.dataset.price}`;
    } else {
      this.confirm.disabled = true;
      this.baseLabel = this.labels.soldOut;
    }
    this.setLabel(this.baseLabel);
    this.setCardPrice(swatch.dataset.price);
  }

  /**
   * The card's own price line ("From $39.00") reflects the selected variant
   * once one is chosen. Only the text nodes change; the theme's visually
   * hidden label inside the element is left in place.
   */
  setCardPrice(price) {
    if (!price) return;
    const card = this.closest('product-grid-item');
    const priceEl = card && card.querySelector('.product__grid__price .price');
    if (!priceEl) return;
    // Everything but the screen-reader label goes: the "From" prefix, the
    // price text, and the <span class="money"> the theme's currency script
    // wraps around it.
    Array.from(priceEl.childNodes)
      .filter((node) => !(node.nodeType === Node.ELEMENT_NODE && node.classList.contains('sr-only')))
      .forEach((node) => node.remove());
    priceEl.appendChild(document.createTextNode(price));
  }

  /* ------------------------------------------------------------- add state */

  onState(state) {
    this.loading = Boolean(state.loading);

    if (state.success && !this.wasSuccess) this.onSuccess();
    this.wasSuccess = Boolean(state.success);
  }

  onSuccess() {
    this.setLabel(`${this.labels.added} ✓`);
    window.clearTimeout(this.labelTimer);
    this.labelTimer = window.setTimeout(() => this.setLabel(this.baseLabel), this.addedDuration);

    const parts = [this.productTitle];
    if (this.variantTitle) parts.push(this.variantTitle);
    parts.push(this.labels.addedToCart);
    this.announce(parts.join(', '));

    if (this.panel) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.close(false), this.closeDelay);
    }
  }

  setLabel(text) {
    if (this.confirmLabel) this.confirmLabel.textContent = text;
  }

  announce(message) {
    if (!this.live) return;
    window.clearTimeout(this.liveTimer);
    this.live.textContent = '';
    // A short gap guarantees assistive tech sees a change even if the same
    // message is announced twice in a row.
    window.setTimeout(() => {
      this.live.textContent = message;
    }, 50);
    this.liveTimer = window.setTimeout(() => {
      this.live.textContent = '';
    }, LIVE_CLEAR_MS);
  }
}

if (!window.customElements.get('grid-quick-add')) {
  window.customElements.define('grid-quick-add', GridQuickAdd);
}
